import * as client from '@/utils/restClient'
import { dateToStringWithFormat } from '@/utils/dateFormat'
import { toSnakeCase } from '@/utils/string'
import { toSnakeCaseObject } from '@/utils/object'
import { string } from 'prop-types'

export const getMonthlyReserve = (year: number, month: number) => {
  const params = {
    year,
    month,
  }
  return client.post<IReserveSummary[]>(
    '/schedules/index_month_switch_json',
    params,
  )
}

interface IGetOptionAlertParams {
  js_start_date_dummy: string
  js_end_date_dummy: string
  reserve: {
    id: number
    'actual_start_at(1i)': string
    'actual_start_at(2i)': string
    'actual_start_at(3i)': string
    'actual_start_at(4i)': string
    'actual_start_at(5i)': string
    'actual_end_at(1i)': string
    'actual_end_at(2i)': string
    'actual_end_at(3i)': string
    'actual_end_at(4i)': string
    'actual_end_at(5i)': string
  }
}

interface IGetOptionAlertResponse {
  result: boolean
}

export const getOptionAlert = (
  reserve: IReserveSummary,
  params: IGetOptionAlertParams,
) => {
  return client.patch<IGetOptionAlertResponse>(
    `/schedules/${reserve.slug}/option_alert`,
    params,
  )
}

interface IGetPriceInfoDetailParams {
  js_start_date_dummy: string
  js_end_date_dummy: string
  reserve: {
    id: number
    'actual_start_at(1i)': string
    'actual_start_at(2i)': string
    'actual_start_at(3i)': string
    'actual_start_at(4i)': string
    'actual_start_at(5i)': string
    'actual_end_at(1i)': string
    'actual_end_at(2i)': string
    'actual_end_at(3i)': string
    'actual_end_at(4i)': string
    'actual_end_at(5i)': string
    actual_options: IReserveOption[]
    actual_transportation_price: number
  }
}

export const getPriceInfoDetail = (
  reserve: IReserveSummary,
  params: IGetPriceInfoDetailParams,
) => {
  return client.patch<string>(
    `/reserves/${reserve.slug}/price_info_detail`,
    params,
  )
}

/*
 * 完了報告
 */

export const getTmpSave = (reserve: IReserveSummary) => {
  return client.get(`/schedules/${reserve.id}/get_report_input_tmp_save_v2`)
}

export const buildReserveOption = (option: IOption): IReserveOption => {
  if (!!option.time) {
    return {
      ...option,
      unit_type: 'time',
    }
  } else {
    return {
      ...option,
      unit_type: 'minute',
      minute: option.minute % 60,
      hour: Math.floor(option.minute / 60),
    }
  }
}

interface ITmpSaveEventResponseResponse {
  result: boolean
  message: string
  errors: string[]
}

export const tmpSaveSittingReportStep1 = (
  reserve: IReserveSummary,
  actual_options: IReserveOption[],
  isTokyoBsWaiting: boolean,
  validation = true,
  additionalChildOption?: IAdditionalChildOptionState,
  comment?: string,
  messageOnReportBack?: string,
) => {
  const params = isTokyoBsWaiting
    ? {
        reserve: {
          id: reserve.id,
          actual_start_at: reserve.actual_start_at,
          actual_end_at: reserve.actual_end_at,
          actual_options: [...actual_options],
          actual_transportation_price: reserve.actual_transportation_price,
          actual_transportation_price_description:
            reserve.actual_transportation_price_description,
          ...(additionalChildOption && {
            additional_child_option_attributes: {
              id: additionalChildOption.id,
              'actual_apply_start_at(1i)': additionalChildOption.year,
              'actual_apply_start_at(2i)': additionalChildOption.month,
              'actual_apply_start_at(3i)': additionalChildOption.day,
              'actual_apply_start_at(4i)': additionalChildOption.hour,
              'actual_apply_start_at(5i)': additionalChildOption.minute,
            },
          }),
          message_on_report_back: messageOnReportBack,
        },
        reserve_comment: {
          comment,
        },
        support_events: {
          validation,
        },
      }
    : {
        reserve: {
          id: reserve.id,
          actual_start_at: reserve.actual_start_at,
          actual_end_at: reserve.actual_end_at,
          actual_options: [...actual_options],
          actual_transportation_price: reserve.actual_transportation_price,
          actual_transportation_price_description:
            reserve.actual_transportation_price_description,
          message_on_report_back: messageOnReportBack,
        },
        reserve_comment: {
          comment,
        },
        support_events: {
          validation,
        },
      }

  return client.patch<ITmpSaveEventResponseResponse>(
    `/schedules/${reserve.id}/report_input_tmp_save_v2`,
    params,
  )
}

interface ITmpSaveSittingReportStep2 {
  result: boolean
  message: string
  errors: string[]
}

export const buildTmpSaveEventParams = (
  events: ISupportEvent[],
  defaultStartDate: Date,
): IEventApi[] => {
  const params: IEventApi[] = events.map(
    ({ startDate, startHour, startMinute, ...event }) => {
      let startTime: string
      if (startDate && startHour && startMinute) {
        const startDay = dateToStringWithFormat(startDate, 'yyyy-MM-dd')
        startTime = `${startDay} ${startHour}:${startMinute}:00.000+09:00`
      } else {
        startTime = dateToStringWithFormat(
          defaultStartDate,
          'yyyy-MM-dd HH:mm:00.000+09:00',
        )
      }

      return {
        ...toSnakeCaseObject(event),
        event_type: toSnakeCase(event.eventType),
        start_time: startTime,
      }
    },
  )
  return params
}

export const tmpSaveSittingReportStep2 = (
  id: number,
  slug: string,
  options: IReserveOption[],
  events: IEventApi[],
  validation = true,
  sittingReportChildrenMood?: string,
  comment?: string,
  tokyoBsTmpUseReport?: string,
  messageOnReportBack?: string,
) => {
  const getEventDateTime = (): string => {
    const now = new Date()
    const jstOffset = 9 * 60 * 60 * 1000
    const jstDate = new Date(now.getTime() + jstOffset)

    return (
      jstDate.toISOString().replace(/[-:T]/g, '').slice(0, 14) +
      jstDate.getMilliseconds().toString().padStart(3, '0')
    )
  }
  const eventDateTime = getEventDateTime()

  const params = {
    reserve: {
      id: id,
      actual_options: [...options],
      sitting_report_children_mood: sittingReportChildrenMood,
      tokyo_bs_tmp_use_report: tokyoBsTmpUseReport,
      message_on_report_back: messageOnReportBack,
    },
    support_events: {
      event_datetime: eventDateTime,
      validation,
      events: events,
    },
    reserve_comment: {
      comment,
    },
  }
  return client.patch<ITmpSaveSittingReportStep2>(
    `/schedules/${slug}/report_input_tmp_save_v2`,
    params,
  )
}

// サポート中の写真
interface IUploadSittingPhotoResponse {
  message: string
  result: boolean
  photo: string
}

export const uploadSittingPhoto = (slug: string, file: File) => {
  const formData = new FormData()
  formData.append('image', file)

  return client.postFile<IUploadSittingPhotoResponse>(
    `/schedules/${slug}/sitting_photo`,
    formData,
  )
}

interface IDeleteSittingPhotoResponse {
  message: string
  result: boolean
}

export const deleteSittingPhoto = (
  reserveSlug: string,
  sittingPhotoSlug: string,
) => {
  return client.deleteRequest<IDeleteSittingPhotoResponse>(
    `/schedules/${reserveSlug}/sitting_photo.json?photo_id=${sittingPhotoSlug}`,
  )
}
